/**
 * Help section copy (Portuguese Brazil). Same keys as helpSections.en.ts
 */
import type { HelpSectionMap } from './helpSections.en';

const helpSectionsPtBR: HelpSectionMap = {
  'getting-started': {
    intro:
      'Aqui está o essencial para começar. Siga estes passos ou explore a barra lateral.',
    stepsTitle: 'Seus primeiros passos',
    step1: 'Abra **Visão geral** para ver o painel e a atividade recente.',
    step2: 'Abra **Minhas tarefas** para ver todos os itens atribuídos a você.',
    step3: 'Clique em um **Projeto** na barra lateral para abrir o quadro, backlog, roadmap e mais.',
    stepPm:
      'Abra **Gestão de projetos** para ver todos os projetos e uma visão de roadmap (estágio atual, próxima implantação, data planejada).',
    step4: 'Use o botão **Criar** no cabeçalho para adicionar um item.',
    step5:
      'Use o **alternador de tema** (sol/lua) no cabeçalho para modo claro ou escuro.',
    outro:
      'Use os links à esquerda para ir a qualquer tópico, ou o tour rápido acima.',
  },
  notifications: {
    intro:
      'O ícone de sino mostra notificações no app. Clique para abrir o centro e marcar como lidas.',
    whatTitle: 'Sobre o que você é notificado',
    n1: 'Quando alguém atribui um item a você',
    n2: 'Quando você é **@mencionado** em um comentário',
    n3: 'Mudanças de status, comentários e lembretes de prazo',
    n4: 'Convites de projeto (se sua organização usar)',
    prefsTitle: 'Preferências',
    prefsBody:
      'No perfil ou nas configurações da conta, escolha quais notificações recebe por e-mail e no app (ex.: só atribuições ou também menções).',
    sessionsBody:
      'No perfil você também vê **Sessões ativas** e pode sair em outros dispositivos.',
    tip: 'Abra o sino e clique em «Marcar tudo como lido» para limpar a lista.',
  },
  'keyboard-shortcuts': {
    intro:
      'Use com o conteúdo principal em foco. No Mac **Cmd**, no Windows/Linux **Ctrl**.',
    k1: '**Cmd/Ctrl + K** ou **/** – Buscar itens e projetos (spotlight). Desativado com modal de item aberto.',
    k2: '**Cmd/Ctrl + I** – Abrir criar item',
    k3: '**Cmd/Ctrl + Enter** – Enviar o formulário de criar item',
    k4: '**Cmd/Ctrl + S** – Salvar (no editor de documentos)',
    k5: '**Escape** – Fechar modal ou busca',
    tip: 'Pressione / ou Cmd/Ctrl + K para buscar sem sair da página. A lista usa rolagem virtualizada para listas grandes.',
  },
  projects: {
    intro:
      'Projetos reúnem itens, sprints e relatórios. Abra qualquer projeto do qual você é membro pela barra lateral.',
    createTitle: 'Como criar um projeto',
    createBody:
      'Vá a **Gestão de projetos** e clique em **Criar projeto**. Adicione nome, descrição e categoria. Você pode usar um modelo para preencher fluxos e configurações.',
    membersTitle: 'Membros',
    membersLead:
      'Abra **Gestão de projetos** para ver todos os projetos, exportar CSV e a **Visão de roadmap** (estágio, próxima implantação, data por projeto). Abra um projeto e use **Gerenciar membros** para:',
    m1: 'Adicionar pessoas e definir função (Admin, Membro ou Visualizador).',
    m2:
      'Adicionar um **grupo** (Configurações de administração → Grupos) para que todos tenham a mesma função.',
    m3: 'Alterar função ou remover alguém.',
    rolesHint:
      'Visualizador = só leitura. Membro = editar itens e comentar. Admin = membros e configurações. É necessário pelo menos um admin do projeto.',
    notePlan:
      'A criação pode ser limitada pelo plano da organização (Free, Pro, Enterprise). Veja Planos e limites abaixo.',
    memberOnly:
      'Somente administradores e gestores de projeto podem criar projetos. Peça ao admin ou use o fluxo de solicitação de acesso.',
  },
  issues: {
    intro:
      'Itens são o trabalho que você acompanha: tarefas, bugs, histórias, épicos e subtarefas.',
    createTitle: 'Como criar um item',
    c1: 'Clique em **Criar** no cabeçalho.',
    c2: 'Escolha **Criar item** ou **Criação em massa via CSV**.',
    c3: 'Preencha o formulário (projeto, título, tipo, prazo etc.) e envie.',
    createTip: 'Você também pode criar no quadro do projeto ou no backlog.',
    typesTitle: 'Tipos de item',
    typeTask: '**Tarefa** – Trabalho geral',
    typeStory: '**História** – Funcionalidade voltada ao usuário',
    typeBug: '**Bug** – Algo a corrigir',
    typeEpic: '**Épico** – Grande iniciativa; outros itens podem vincular',
    typeSub: '**Subtarefa** – Filha de outro item',
    fieldsTitle: 'O que você pode definir',
    fieldsBody:
      'Título, descrição, responsável, prioridade, status e prazo são o básico. Também rótulos, componentes, versões, vínculo pai ou épico, estimativas e anexos. Ao criar, você pode adicionar um **item vinculado** e o tipo de vínculo (ex.: Bloqueia, Bloqueado por, Relaciona a, Duplica); o tipo fica como padrão para o próximo vínculo. Abra um item para editar tudo e comentar.',
  },
  backlog: {
    intro:
      'Veja itens fora de um sprint e gerencie sprints. Arraste para reordenar o backlog ou colocar em um sprint.',
    b1: 'Arraste itens para um sprint para planejar.',
    b2: 'Crie sprints e mova de planejado → ativo → concluído.',
    b3: 'Devolva itens ao backlog se necessário.',
    b4:
      'Backlogs grandes usam rolagem virtualizada; o arrastar mantém a mesma ordenação.',
  },
  'project-management': {
    intro:
      'Para administradores e gestores de projeto. Abra **Gestão de projetos** na barra lateral para ver todos os projetos e o roadmap de alto nível.',
    allTitle: 'Todos os projetos',
    allBody:
      'A tabela lista cada projeto da organização. Você pode criar projeto, exportar CSV e abrir quadro, backlog, roadmap ou documentos. Use **Gerenciar membros** por projeto.',
    archivedBody:
      'Ative **Mostrar arquivados** para incluir projetos arquivados. Admins da org e do projeto arquivam ou restauram em **Ações** — arquivados ficam fora da lista padrão e da maioria dos seletores até restaurar.',
    roadmapTitle: 'Visão de roadmap',
    roadmapLead:
      'No topo, a **Visão de roadmap** mostra, por projeto:',
    ro1: '**Estágio atual** – Última versão lançada (se houver).',
    ro2: '**Próxima implantação** – Nome da próxima versão planejada.',
    ro3: '**Data planejada** – Data de lançamento dessa versão quando definida.',
    roadmapFoot:
      'Assim você vê o estado de cada projeto e o próximo release. Clique no nome do projeto ou **Ver roadmap completo** para o roadmap entre projetos ou do projeto.',
    roadmapNote:
      'Versões e datas são configuradas por projeto (Configurações de administração ou do projeto). Adicione versões e **Versão de correção** nos itens para preencher o roadmap.',
  },
  roadmap: {
    intro:
      'Roadmaps mostram versões e entregas. O FlowBoard tem roadmap por projeto e outro entre projetos para toda a organização.',
    projTitle: 'Roadmap do projeto',
    projLead: 'Abra um projeto e escolha **Roadmap** na barra lateral. Você verá:',
    pr1:
      'Um cartão com **Estágio atual**, **Próxima implantação** e **Data planejada**.',
    pr2:
      '**Visualização em lista** – Linha do tempo de versões com descrições, datas de início/lançamento e itens (Versão de correção / Afeta versão).',
    pr3:
      '**Visualização em linha do tempo** – Versões como barras; arraste selos de item entre versões para mudar a **Versão de correção**.',
    fixBody:
      'Adicione versões no projeto (ou org) e defina **Versão de correção** / Afeta versão nos itens.',
    crossTitle: 'Roadmap entre projetos',
    crossBody:
      'Na barra lateral, **Roadmap entre projetos** lista todas as versões de todos os projetos. Cada linha mostra projeto, versão, data e contagens (Correção / Afeta). Clique em um projeto para abrir seu roadmap.',
    tip: 'Use o roadmap para alinhar partes interessadas sobre o estado e a próxima implantação. Defina datas de início e lançamento nas versões para linhas do tempo precisas.',
  },
  calendar: {
    body: 'Itens com prazo aparecem no calendário. Troque o mês para planejar ou ver atrasos.',
  },
  'list-view': {
    intro:
      'Tabela de todos os itens do projeto. Ordene, filtre e edite em massa status ou responsável.',
    lv1: 'Use filtros avançados por status, responsável, rótulo etc.',
    lv2: 'Selecione vários itens para mudar status, responsável ou excluir de uma vez.',
    lvBulk:
      'A edição em massa atualiza muitos itens. Na lista de um projeto você também pode definir rótulos, componentes, versões de correção e resolução.',
    lv3:
      'Em listas grandes use **paginação** embaixo para mudar de página e o tamanho da página.',
    lv4:
      '**Layout salvo** guarda filtro, busca, ordenação e colunas neste navegador (além dos filtros salvos no servidor). Ative a coluna **SLA** para ver violações ativas.',
    tip: 'A lista é ideal para comparar muitos itens ou fazer atualizações em massa.',
  },
  'dependency-graph': {
    body: 'Mapa visual de como os itens se vinculam (bloqueia, bloqueado por, relaciona). Útil para impacto e caminhos críticos.',
  },
  docs: {
    intro:
      'Cada projeto tem **Documentos** (estilo Confluence) para especificações e atas. Abra um projeto e vá a **Documentos** na barra lateral.',
    tplTitle: 'Modelos e rótulos',
    tplBody:
      'Ao criar ou editar, você pode escolher um **modelo** (ex.: Atas, Spec) e **rótulos** para organizar e buscar.',
    sidebarTitle: 'Barra lateral esquerda',
    sidebarBody:
      'A árvore de páginas aparece à esquerda. **Nova página** para criar; caixa de busca por título. Expanda ou recolha pastas; clique no título para abrir.',
    pageTitle: 'Na página',
    d1: '**Nesta página** – Índice fixo a partir de títulos H2/H3 ao visualizar (não ao editar).',
    d2: '**Páginas filhas** – Se houver subpáginas, aparecem abaixo com links.',
    d3: '**Editar** – Título, página pai e conteúdo. Barra de ferramentas para formatação, links e imagens.',
    d4: '**Histórico** – Ver e restaurar versões anteriores.',
    d5: '**Exportar** – Baixar a página como Markdown.',
    d6: 'Comentários ficam embaixo; adicione e leia respostas.',
    tip: 'Pressione **Ctrl + S** no editor para salvar sem sair.',
  },
  reports: {
    intro:
      'Em um projeto, abra **Relatórios** para velocidade, fluxo cumulativo, gráficos de controle, progresso de épico, tempo, construtor de relatórios e a visualização **Workflow** (Gantt). Exporte dados ou o gráfico para compartilhar.',
    typesTitle: 'Tipos de relatório',
    r1: '**Velocidade** – Trabalho concluído por sprint.',
    r2: '**Fluxo cumulativo** – Como os itens se movem entre status ao longo do tempo.',
    r3: '**Gráfico de controle** – Tempo de ciclo de itens concluídos.',
    r4:
      '**Relatório de épico** – Use a lista **Épico** para um épico ou todos. Exporte pelo botão.',
    r5: '**Registro de tempo** – Estimado versus gasto por tipo e por usuário.',
    r6:
      '**Workflow (Gantt)** – Linha do tempo com prazos: hierarquia (épicos → tarefas → subtarefas), barras de progresso, linhas de dependência (pai-filho e vínculos como «bloqueia») e marcos (mesmo dia como losangos). Zoom (Dia / Semana / Mês) e **Exportar como imagem** em PNG. Cores conforme status do projeto.',
    r7: '**Construtor de relatórios** – Agrupe por status, tipo, prioridade ou responsável.',
    tip: 'Use velocidade e fluxo cumulativo para entender a vazão do time e gargalos.',
  },
  dashboards: {
    body: 'Vá a **Painéis personalizados** para montar seu painel com widgets (por projeto, grupo ou responsável). Controle quem vê cada painel por grupo ou função.',
  },
  'admin-settings': {
    intro:
      'Abra **Configurações de administração** na barra lateral para a organização e a configuração de projetos.',
    orgTitle: 'Organização',
    orgBody: 'Edite o nome da org, membros e convites pendentes.',
    projCfgTitle: 'Configuração de projetos',
    projCfgBody:
      'Gerencie **Rótulos**, **Componentes**, **Versões** e **Campos personalizados** nos projetos.',
    tplTitle: 'Modelos',
    tplBody:
      'Modelos de **projeto** e **item** definem fluxos e campos padrão.',
    autoTitle: 'Automação',
    autoBody:
      'Regras quando os itens atendem condições (ex.: mudança de status, responsável definido). Gatilhos e ações. Ícone **Editar** (lápis) na tabela para nome, descrição, gatilho ou ativo; **Excluir** remove a regra.',
    groupsTitle: 'Grupos',
    groupsBody:
      'Crie grupos, adicione membros e atribua a projetos (em Gerenciar membros) para a mesma função no projeto.',
    rolesTitle: 'Funções e registro de auditoria',
    rolesBody: 'Funções personalizadas e registro de auditoria de mudanças importantes.',
    systemTitle: 'Sistema (somente superadmin)',
    systemBody:
      'A aba **Sistema** lista organizações. Você pode definir o **plano** (Free, Pro, Enterprise) e convidar empresas.',
  },
  glossary: {
    intro: 'Definições breves de termos no FlowBoard.',
    g1: '**Item** – Trabalho: tarefa, história, bug, épico ou subtarefa.',
    g2: '**Épico** – Grande iniciativa; outros itens podem vincular.',
    g3: '**Sprint** – Conjunto de itens em um período (ex.: 2 semanas).',
    g4: '**Backlog** – Itens ainda não em um sprint.',
    g5: '**Versão** – Entrega ou marco (ex.: v1.0). Datas opcionais; pode marcar como lançada.',
    g6: '**Versão de correção** – Versão em que o item será corrigido ou entregue. Aparece no roadmap.',
    g7: '**Afeta versão** – Versões que o item afeta (ex.: bugs). Também no roadmap.',
    g8: '**Quadro Kanban** – Colunas por status; arraste cartões.',
    g9: '**Fluxo de trabalho** – Status e transições (ex.: A fazer → Em andamento → Concluído).',
    g10: '**Responsável** – Pessoa responsável pelo item.',
    g11: '**Observadores** – Usuários notificados sobre atualizações.',
  },
  'subscription-plans': {
    body: 'O plano Free limita projetos e usuários; planos superiores permitem mais (incluindo ilimitado). Ao atingir o limite, a criação fica bloqueada até atualizar. Superadmins definem o plano em Configurações de administração → Sistema.',
  },
  'api-integrations': {
    intro:
      'O FlowBoard expõe uma API REST para itens, projetos, relatórios, webhooks e mais. Use a URL base da instância com um token válido (ex.: após login).',
    webhooksTitle: 'Webhooks',
    webhooksBody:
      'Admins da org configuram webhooks de saída em **Admin → Webhooks**. Enviam POST HTTP em eventos de itens e projetos; opcionalmente segredo HMAC. Para Slack, adicione um webhook com a URL do Slack (ou regras de automação com ação Webhook).',
    openapiNote:
      'Especificação OpenAPI 3.0 em **/api/docs/openapi.json** com o backend em execução; use com Swagger ou outras ferramentas.',
  },
};

export default helpSectionsPtBR;
